import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { adminAPI, authAPI } from '@/utils/api';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { ArrowLeft, Plus, Edit, Trash2 } from 'lucide-react';
import { toast } from 'sonner';
import { Loader } from '@/components/ui/loader';

export const AdminContent = () => {
  const navigate = useNavigate();
  const [stats, setStats] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null);
  const [formData, setFormData] = useState({
    key: '',
    label: { en: '', hi: '' },
    value: '',
    order: 0
  });

  const checkAuth = useCallback(async () => {
    try {
      await authAPI.getMe();
      fetchStats();
    } catch (error) {
      navigate('/admin/login');
    }
  }, [navigate]);

  useEffect(() => {
    checkAuth();
  }, [checkAuth]);

  const fetchStats = async () => {
    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/content/stats`);
      const data = await response.json();
      setStats(data);
    } catch (error) {
      console.error('Error fetching stats:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editing) {
        await adminAPI.updateStat(editing, formData);
        toast.success('Stat updated successfully');
      } else {
        await adminAPI.createStat(formData);
        toast.success('Stat created successfully');
      }
      setFormData({ key: '', label: { en: '', hi: '' }, value: '', order: 0 });
      setEditing(null);
      fetchStats();
    } catch (error) {
      toast.error('Error saving stat');
    }
  };

  const handleEdit = (stat) => {
    setFormData(stat);
    setEditing(stat.key);
  };

  const handleDelete = async (key) => {
    if (window.confirm('Are you sure you want to delete this stat?')) {
      try {
        await adminAPI.deleteStat(key);
        toast.success('Stat deleted');
        fetchStats();
      } catch (error) {
        toast.error('Error deleting stat');
      }
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-100">
        <Loader className="text-secondary" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-100 admin-panel">
      <header className="bg-white shadow-sm border-b">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <Link to="/admin/dashboard">
                <Button variant="ghost" size="sm">
                  <ArrowLeft size={18} className="mr-2" />
                  Back
                </Button>
              </Link>
              <h1 className="text-2xl font-heading font-bold">Manage Performance Stats</h1>
            </div>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
          {/* Form */}
          <div className="bg-white rounded-lg shadow-sm border p-6">
            <h2 className="text-xl font-semibold mb-6">{editing ? 'Edit' : 'Add'} Stat</h2>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <label className="block text-sm font-medium mb-2">Key (Unique ID)</label>
                <Input
                  value={formData.key}
                  onChange={(e) => setFormData({ ...formData, key: e.target.value })}
                  disabled={editing}
                  required
                  placeholder="e.g., entities_managed"
                />
              </div>

              <div>
                <label className="block text-sm font-medium mb-2">Label (English)</label>
                <Input
                  value={formData.label.en}
                  onChange={(e) => setFormData({ ...formData, label: { ...formData.label, en: e.target.value } })}
                  required
                  placeholder="e.g., Corporate Entities Managed"
                />
              </div>

              <div>
                <label className="block text-sm font-medium mb-2">Label (Hindi)</label>
                <Input
                  value={formData.label.hi}
                  onChange={(e) => setFormData({ ...formData, label: { ...formData.label, hi: e.target.value } })}
                  required
                  placeholder="Hindi translation"
                />
              </div>

              <div>
                <label className="block text-sm font-medium mb-2">Value</label>
                <Input
                  value={formData.value}
                  onChange={(e) => setFormData({ ...formData, value: e.target.value })}
                  required
                  placeholder="e.g., 5+, ₹20 Crore, 80%"
                />
              </div>

              <div>
                <label className="block text-sm font-medium mb-2">Display Order</label>
                <Input
                  type="number"
                  value={formData.order}
                  onChange={(e) => setFormData({ ...formData, order: parseInt(e.target.value) })}
                  required
                />
              </div>

              <div className="flex gap-2">
                <Button type="submit" className="flex-1">
                  {editing ? 'Update' : 'Create'} Stat
                </Button>
                {editing && (
                  <Button
                    type="button"
                    variant="outline"
                    onClick={() => {
                      setEditing(null);
                      setFormData({ key: '', label: { en: '', hi: '' }, value: '', order: 0 });
                    }}
                  >
                    Cancel
                  </Button>
                )}
              </div>
            </form>
          </div>

          {/* Stats List */}
          <div className="bg-white rounded-lg shadow-sm border p-6">
            <h2 className="text-xl font-semibold mb-6">Current Stats</h2>
            <div className="space-y-4">
              {stats.length === 0 ? (
                <p className="text-gray-500 text-center py-8">No stats yet</p>
              ) : (
                stats.map((stat) => (
                  <div key={stat.key} className="border rounded-lg p-4 hover:shadow-md transition-shadow">
                    <div className="flex justify-between items-start mb-2">
                      <div>
                        <h3 className="font-semibold text-lg">{stat.value}</h3>
                        <p className="text-sm text-gray-600">{stat.label.en}</p>
                        <p className="text-xs text-gray-500">Order: {stat.order}</p>
                      </div>
                      <div className="flex gap-2">
                        <Button size="sm" variant="ghost" onClick={() => handleEdit(stat)}>
                          <Edit size={16} />
                        </Button>
                        <Button size="sm" variant="ghost" onClick={() => handleDelete(stat.key)} className="text-red-600">
                          <Trash2 size={16} />
                        </Button>
                      </div>
                    </div>
                  </div>
                ))
              )}
            </div>
          </div>
        </div>
      </main>
    </div>
  );
};